import { AppstoreAddOutlined } from '@ant-design/icons';
import { Form, Layout, Spin } from 'antd';
import dayjs from 'dayjs';
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useTransition,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useStore } from 'zustand';

import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import {
  useGetHotelInventory,
  useSaveHotelInventory,
  useValidateHotelInventory,
} from 'app/hooks/data/hotels/extranet/useInventory';
import { useHotelUnavailable } from 'app/hooks/data/hotels/useHotelUnavailable';
import { useHotelScore } from 'app/hooks/data/useHotelScore';
import { useSales } from 'app/hooks/data/useSales';
import { useLocalStorage } from 'app/hooks/useLocalStorage';
import type { BulkEditInitialValues } from 'app/typings/bulkEdit';
import { ChannelManager } from 'app/typings/channelManagers';
import { formatDateWithoutHour } from 'app/utils/dates';
import { amplitudeTrack, useTrackViewPage } from 'app/utils/track/amplitude';

import { EmptyState } from '../../commons/EmptyState/EmptyState';
import { ExtranetHeader } from '../../commons/ExtranetHeader/ExtranetHeader';
import { ChannelManagerInfo } from '../_common/ChannelManagerInfo/ChannelManagerInfo';

import { AutoInventoryModal } from './AutoInventory/AutoInventoryModal/AutoInventoryModal';
import { BulkEdit } from './BulkEdit/BulkEdit';
import './ExtranetInventory.scss';
import { HotelScore } from './HotelScore/HotelScore';
import { InventoryBanner } from './InventoryBanner/InventoryBanner';
import { inventoryStore } from './InventoryStore';
import { PricingRule } from './PricingRule/PricingRule';
import { InventoryHeader } from './_components/InventoryHeader/InventoryHeader';
import { InventorySection } from './_components/InventorySection/InventorySection';
import { MaxBookingConfigurationModal } from './_components/MaxBookingConfigurationModal/MaxBookingConfigurationModal';
import { SaveInventoryModal } from './_components/SaveInventoryModal/SaveInventoryModal';
import { ValidationModal } from './_components/ValidationModal/ValidationModal';
import { Overview } from './_content/Overview';
import { Room } from './_content/Room';
import type { FormRoom, FormType } from './type';
import { handleFieldsChange } from './utils';

const PAGE = 'Inventory';

const NB_PACKAGES_X_ROOMS_PERF_BREAKPOINT = 50;

const Inventory = () => {
  const [isPending, startTransition] = useTransition();

  const { value: userActivity, setValue: setUserActivity } = useLocalStorage<{
    nbVisit: number;
    lastVisit: Date;
  }>('visitCount', { nbVisit: 1, lastVisit: new Date() });

  const { hotelId, hotelInfo, hotelLoading } = useContext(HotelContext);

  const {
    publishedPackages,
    getExperiencesForPackage,
    rooms,
    hasAtLeastOneDayPackage,
    packagesLoading,
    roomsLoading,
  } = useContext(LegacyHotelContext);

  const { setPage } = useContext(TaskCenterContext);

  const [form] = Form.useForm();

  const { data: sales, isLoading: isSalesLoading } = useSales();

  const isManualHotel =
    hotelInfo &&
    (!hotelInfo.channelManager ||
      hotelInfo.channelManager === ChannelManager.NONE);

  const { data: hotelScore } = useHotelScore(hotelId);

  const timePeriod = useStore(inventoryStore, (state) => state.timePeriod);
  const formTimePeriod = useStore(
    inventoryStore,
    (state) => state.formTimePeriod
  );
  const saveChangesModal = useStore(
    inventoryStore,
    (state) => state.saveChangesModal
  );
  const stateInventory = useStore(inventoryStore, (state) => state.inventory);
  const setFormHasChanged = useStore(
    inventoryStore,
    (state) => state.setFormHasChanged
  );
  const setInventoryErrors = useStore(
    inventoryStore,
    (state) => state.setInventoryErrors
  );
  const setInventorySaved = useStore(
    inventoryStore,
    (state) => state.setInventorySaved
  );
  const setForm = useStore(inventoryStore, (state) => state.setForm);
  const setSelectPackage = useStore(
    inventoryStore,
    (state) => state.setSelectPackage
  );
  const selectedPackages = useStore(
    inventoryStore,
    (state) => state.selectPackage
  );
  const updateInventory = useStore(
    inventoryStore,
    (state) => state.updateInventory
  );
  const setBookableDays = useStore(
    inventoryStore,
    (state) => state.setBookableDays
  );
  const openEditMaxBooking = useStore(
    inventoryStore,
    (state) => state.openEditMaxBooking
  );
  const setIsBulkEditOpen = useStore(
    inventoryStore,
    (state) => state.setIsBulkEditOpen
  );
  const setBulkEditInitialValues = useStore(
    inventoryStore,
    (state) => state.setBulkEditInitialValues
  );
  const resetInventory = useStore(
    inventoryStore,
    (state) => state.resetInventory
  );
  const isBulkEditOpen = useStore(
    inventoryStore,
    (state) => state.isBulkEditOpen
  );
  const bulkEditInitialValues = useStore(
    inventoryStore,
    (state) => state.bulkEditInitialValues
  );

  const isPricingRuleModalOpen = useStore(
    inventoryStore,
    (state) => state.pricingRuleModalOpen
  );

  const setIsPricingRuleModalOpen = useStore(
    inventoryStore,
    (state) => state.setPricingRuleModalOpen
  );

  const isAutoInventoryModalOpen = useStore(
    inventoryStore,
    (state) => state.autoInventoryModalOpen
  );

  const setIsAutoInventoryModalOpen = useStore(
    inventoryStore,
    (state) => state.setAutoInventoryModalOpen
  );

  const setIsEditMode = useStore(
    inventoryStore,
    (state) => state.setIsEditMode
  );

  const { data: inventory, isFetching: inventoryLoading } =
    useGetHotelInventory(
      formatDateWithoutHour(timePeriod.startDate),
      timePeriod,
      getExperiencesForPackage,
      publishedPackages,
      hasAtLeastOneDayPackage,
      hotelInfo
    );

  const hiddenInventorySpinnerBreakpoint =
    selectedPackages.length * rooms.length >
    NB_PACKAGES_X_ROOMS_PERF_BREAKPOINT;

  const showSpinner = inventoryLoading || isPending;

  const packageHaveMinDiscount = publishedPackages.find(
    (pkg) =>
      pkg.minimumDiscountOverride && pkg.minimumDiscountOverrideDates.length > 0
  );
  const minDiscountDateAreInFuture =
    packageHaveMinDiscount?.minimumDiscountOverrideDates.some(
      (date) =>
        dayjs(date).isAfter(dayjs(), 'day') ||
        dayjs(date).isSame(dayjs(), 'day')
    );

  const showInventoryBanner =
    !!packageHaveMinDiscount && minDiscountDateAreInFuture;

  const { mutateAsync: saveInventory } = useSaveHotelInventory(
    hotelId,
    formatDateWithoutHour(timePeriod.startDate)
  );
  const { mutateAsync: setHotelUnavailable } = useHotelUnavailable(hotelId);

  const history = useHistory();
  const location = useLocation();

  useTrackViewPage({ page: 'inventory' });

  useEffect(() => {
    resetInventory();
  }, [resetInventory]);

  useEffect(() => {
    setPage(PAGE);
  });

  useEffect(() => {
    if (inventory) {
      updateInventory(timePeriod, inventory);
    }
    /* We don't want to listen on timePeriod because we only want 
      to update everything once the corresponding inventory is loaded 
      to avoid multiple rerender
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy directive, needs investigation
  }, [inventory, updateInventory]);

  useEffect(() => {
    setForm(form);
  }, [form, setForm]);

  useEffect(() => {
    if (stateInventory) {
      form.resetFields();
    }
  }, [form, stateInventory]);

  useEffect(() => {
    setSelectPackage(publishedPackages.map((p) => p.id) || []);
  }, [setSelectPackage, publishedPackages]);

  useEffect(() => {
    if (!!sales && !isSalesLoading) {
      const bookableDays =
        sales.find((sale) => sale.countryId === hotelInfo?.countryId)
          ?.bookableDays || [];

      setBookableDays(bookableDays);
    }
  }, [hotelInfo?.countryId, isSalesLoading, sales, setBookableDays]);

  useEffect(() => {
    const urlQueries = new URLSearchParams(location.search);

    if (urlQueries.has('pricingRuleModalOpen')) {
      setIsPricingRuleModalOpen(true);
      urlQueries.delete('pricingRuleModalOpen');
      history.replace({
        search: '?' + urlQueries.toString(),
      });
    }

    if (urlQueries.has('noStock')) {
      setHotelUnavailable();
      urlQueries.delete('noStock');
      history.replace({
        search: '?' + urlQueries.toString(),
      });
    }
  }, [
    history,
    location.search,
    setIsPricingRuleModalOpen,
    setHotelUnavailable,
  ]);

  const { mutateAsync: validateInventory } = useValidateHotelInventory(
    hotelId,
    formatDateWithoutHour(formTimePeriod.startDate)
  );

  const onSubmit = useCallback(
    async (values: FormType) => {
      const formattedPackages = values.packages
        ? Object.entries(values.packages).map(([packageId, bookings]) => ({
            packageId: parseInt(packageId),
            maxBookings: Object.entries(bookings.bookings)
              .filter(([, maxBooking]) => maxBooking.maxBooking !== undefined)
              .map(([date, maxBooking]) => ({
                date,
                maxBooking: maxBooking.maxBooking ?? undefined,
              })),
          }))
        : [];

      const formattedRooms = Object.entries(values.rooms || {}).map(
        ([roomId, room]) => ({
          roomId: parseInt(roomId) || 0,
          inventories: Object.entries(room.plannings).map(
            ([date, planning]) => ({
              ...planning,
              date,
              remainingStock: planning.stock,
              closed: planning.closed ?? false,
              packagePrices: Object.entries(planning.packagePrices).map(
                ([packageId, packagePrice]) => ({
                  ...packagePrice,
                  packageId: parseInt(packageId),
                })
              ),
            })
          ),
        })
      );

      const formattedValues = {
        packages: formattedPackages,
        rooms: formattedRooms,
      };

      setInventorySaved(formattedValues);

      const result = await validateInventory(formattedValues);

      if (result && result.errors && !!result.errors.length) {
        setInventoryErrors(result.errors);
      } else {
        await saveInventory(formattedValues);
        setIsEditMode(false);
        setFormHasChanged(false);
        setInventoryErrors([]);
      }
    },
    [
      saveInventory,
      setFormHasChanged,
      setInventoryErrors,
      setInventorySaved,
      setIsEditMode,
      validateInventory,
    ]
  );

  if (
    formatDateWithoutHour(new Date(userActivity.lastVisit)) !==
    formatDateWithoutHour(new Date())
  ) {
    setUserActivity({
      nbVisit: userActivity.nbVisit + 1,
      lastVisit: new Date(),
    });
  }

  const openBulkEdit = useCallback(
    (
      clickEvent: React.MouseEvent<HTMLElement, MouseEvent>,
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      bulkEditInitialValues: BulkEditInitialValues
    ) => {
      clickEvent.stopPropagation();
      amplitudeTrack('Open Bulk Edit');
      setIsBulkEditOpen(true);
      setBulkEditInitialValues(bulkEditInitialValues);
    },
    [setBulkEditInitialValues, setIsBulkEditOpen]
  );

  const hasMultipleRooms = rooms.length > 1;

  const shouldShowOverview =
    !hasAtLeastOneDayPackage || (hasAtLeastOneDayPackage && hasMultipleRooms);

  const formRender = useMemo(
    () => (
      <Form
        form={form}
        onFinish={onSubmit}
        initialValues={stateInventory}
        onFieldsChange={(changedFields, _allFields) =>
          handleFieldsChange(
            changedFields,
            setFormHasChanged,
            publishedPackages,
            form,
            getExperiencesForPackage,
            hotelInfo?.roomCount || 0,
            hasAtLeastOneDayPackage
          )
        }
      >
        <InventoryHeader startTransition={startTransition} />

        {hiddenInventorySpinnerBreakpoint && showSpinner && (
          <div className="loading-inventory">
            <Spin />
          </div>
        )}

        {showInventoryBanner && (
          <div className="inventory-banner">
            <InventoryBanner />
          </div>
        )}

        {!(showSpinner && hiddenInventorySpinnerBreakpoint) && (
          <>
            {!!hotelScore && !hasAtLeastOneDayPackage ? (
              <div className="inventory-hotel-score">
                <HotelScore
                  isActive={userActivity.nbVisit < 4}
                  isManualHotel={!!isManualHotel}
                  openBulkEdit={(clickEvent) =>
                    openBulkEdit(clickEvent, {
                      days: [5, 6],
                      roomId: undefined,
                    })
                  }
                  stockOptimisationScore={hotelScore.stockOptimisationScore}
                  openAutoInventory={() => setIsAutoInventoryModalOpen(true)}
                />
              </div>
            ) : null}

            {shouldShowOverview && (
              <InventorySection
                title="Overview"
                renderContent={() => <Overview />}
              />
            )}
            <InventorySection
              title={
                hasAtLeastOneDayPackage
                  ? shouldShowOverview
                    ? 'Time slots'
                    : undefined
                  : 'Room details'
              }
              renderContent={() => {
                const roomsValues = Object.values(stateInventory?.rooms ?? {});

                if (publishedPackages.length > 0 && !selectedPackages.length) {
                  return (
                    <div className="empty-state empty-state-no-packages-selected">
                      <EmptyState
                        title="No packages selected"
                        icon={<AppstoreAddOutlined />}
                      />
                    </div>
                  );
                }

                const hasPublishedPackages = publishedPackages.length > 0;
                const hasPublishedRoomsAssociated = publishedPackages.some(
                  (pkg) => pkg.rooms.some((room) => room.published)
                );

                if (!hasPublishedRoomsAssociated) {
                  return (
                    <div className="empty-state empty-state-room">
                      <EmptyState
                        title={
                          hasPublishedPackages ? 'No rooms' : 'No packages'
                        }
                        description={
                          hasPublishedPackages
                            ? 'No rooms associated to the published packages. Contact your AM for more information'
                            : 'You have no published packages. Contact your AM for more information'
                        }
                        icon={<AppstoreAddOutlined />}
                      />
                    </div>
                  );
                }

                rooms.filter((r) =>
                  roomsValues.some((rv) => rv.roomId === r.id)
                );

                // We can't have a correct order on roomsValues since stateInventory.rooms is an object and not an array, so we take the order of rooms and associate both
                const orderedRooms = rooms.reduce((acc, room) => {
                  const inventoryRoom = roomsValues.find(
                    (rv) => rv.roomId === room.id
                  );

                  if (inventoryRoom) {
                    acc.push(inventoryRoom);
                  }

                  return acc;
                }, [] as FormRoom[]);

                return orderedRooms.map((room, index) => {
                  return (
                    <Fragment key={index}>
                      <Room
                        roomId={room.roomId}
                        timePeriod={formTimePeriod}
                        shouldShowOverview={shouldShowOverview}
                      />
                    </Fragment>
                  );
                });
              }}
            />
          </>
        )}
      </Form>
    ),
    [
      form,
      onSubmit,
      stateInventory,
      hiddenInventorySpinnerBreakpoint,
      showSpinner,
      hotelScore,
      hasAtLeastOneDayPackage,
      userActivity.nbVisit,
      isManualHotel,
      shouldShowOverview,
      setFormHasChanged,
      publishedPackages,
      getExperiencesForPackage,
      hotelInfo?.roomCount,
      openBulkEdit,
      setIsAutoInventoryModalOpen,
      selectedPackages.length,
      rooms,
      formTimePeriod,
      showInventoryBanner,
    ]
  );

  if (
    hotelLoading ||
    roomsLoading ||
    packagesLoading ||
    (inventoryLoading && stateInventory === undefined)
  ) {
    return (
      <Layout className="extranet-inventory">
        <div className="inventory-container">
          <ExtranetHeader page={PAGE} />
          <InventoryHeader startTransition={startTransition} />
          <div className="loading-inventory">
            <Spin />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout className="extranet-inventory">
      <Spin spinning={showSpinner && !hiddenInventorySpinnerBreakpoint}>
        <ExtranetHeader
          page={PAGE}
          subTitle={
            <ChannelManagerInfo
              channelManager={hotelInfo?.channelManager}
              supplierId={hotelInfo?.supplierId}
              inventory={stateInventory}
            />
          }
        />
        <div className="inventory-container" key="inventory-container">
          {formRender}
        </div>
      </Spin>
      <BulkEdit
        setIsBulkEditOpen={setIsBulkEditOpen}
        isBulkEditOpen={isBulkEditOpen}
        initialValues={bulkEditInitialValues}
        currentRange={{
          start: timePeriod.startDate,
          end: timePeriod.endDate,
        }}
        hotelId={hotelId}
        autoDiscountRate={hotelInfo?.autoDiscountRate}
      />
      <PricingRule
        onClose={() => setIsPricingRuleModalOpen(false)}
        isOpen={isPricingRuleModalOpen}
      />
      {isAutoInventoryModalOpen && (
        <AutoInventoryModal
          key={`auto-inventory-modal-${hotelId}`}
          hasAutoInventoryActivated={!!hotelInfo?.stockScrapperEnabled}
          isAutoInventoryModalOpen={isAutoInventoryModalOpen}
          onClose={() => setIsAutoInventoryModalOpen(false)}
        />
      )}

      <ValidationModal autoDiscountRate={undefined} />
      {saveChangesModal.isVisible && <SaveInventoryModal />}
      {openEditMaxBooking && <MaxBookingConfigurationModal />}
    </Layout>
  );
};

export const ExtranetInventory = () => {
  const { hotelId } = useContext(HotelContext);

  if (!hotelId) {
    return <></>;
  }

  return <Inventory />;
};

/**
 * Note: Ce composant représente l'ANCIEN système d'inventaire (Legacy)
 *
 * Cette version utilise le modèle de données basé sur les packages (HotelMode.PACKAGE).
 * Elle est affichée pour les hôtels avec isLegacy = true
 *
 * Pour les nouveaux hôtels (NDM), voir ExtranetInventoryNDM
 * Le routage entre les deux versions est géré par InventoryRouter.tsx
 *
 * @see InventoryRouter pour comprendre le mécanisme de routage
 * @see ExtranetInventoryNDM pour la nouvelle version
 */
